import { Component } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MaterialModule } from "../../material/material.module";
import { UIComponentsModule } from "../../ui-components/ui-components.module";
import { NODE_TYPE } from "../../common/linqset-model";
import { ONBOARDING_CHOICE } from "../../user/onboarding";

// the kinds of node a model is drawn with, for the key
const NODE_KEY = [
    {type: NODE_TYPE.ACTION, name: 'Action', text: 'Work that is done, a step in the process'},
    {type: NODE_TYPE.INFORMATION, name: 'Information', text: 'What an action creates or changes, and passes on'},
    {type: NODE_TYPE.ROLE, name: 'Role', text: 'The people who do the work'},
    {type: NODE_TYPE.SYSTEM, name: 'System', text: 'The systems and tools used, or where information is kept'},
]

// shown whenever someone who can change it opens an empty model: what a model is drawn with, and the two ways to start it. It
// closes with CREATE to build the model with AI, as Build model with AI on the toolbar does; with one of the other
// choices for help; or with nothing to draw it on the canvas
@Component({
    selector: 'app-new-model-dialog',
    standalone: true,
    imports: [MaterialModule, UIComponentsModule],
    template: `
        <h2 mat-dialog-title>Start your model</h2>
        <mat-dialog-content class="content">
            <p class="lead">A model shows how a process works: the actions taken, the information they create and pass on, and the people and systems that do them. The main flow, of actions and the information passed between them, is called the supply chain.</p>

            <ul class="key" aria-label="The kinds of node in a model">
                @for (n of nodeKey; track n.type) {
                    <li><app-node-svg [type]="n.type" [height]="30" caller="new model dialog" aria-hidden="true"></app-node-svg><span><b>{{n.name}}</b> {{n.text}}</span></li>
                }
            </ul>

            <h3>How would you like to start?</h3>
            <div class="starts">
                <button type="button" class="start" (click)="close(CREATE)" data-testid="new-model-ai">
                    <mat-icon fontSet="material-symbols-outlined" aria-hidden="true">wand_stars</mat-icon>
                    <span class="start-title">Build it with AI</span>
                    <span class="start-text">Tell AI about the process and it builds the model: the steps, the people and the systems. You can change anything afterwards.</span>
                </button>
                <button type="button" class="start" (click)="close()" data-testid="new-model-draw">
                    <mat-icon aria-hidden="true">draw</mat-icon>
                    <span class="start-title">Draw it yourself</span>
                    <span class="start-text">Start on the blank canvas: add the first action, then the information it creates, and build the flow from there.</span>
                </button>
            </div>
            <p class="note"><mat-icon fontSet="material-symbols-outlined" aria-hidden="true">wand_stars</mat-icon><span>Until you add to the model, you can still have AI build it with <b>Build model with AI</b>, the wand on the toolbar.</span></p>
        </mat-dialog-content>
        <mat-dialog-actions class="actions">
            <span class="help-label">Need help?</span>
            <button mat-button (click)="close(TOUR)">Explain the screen</button>
            <button mat-button (click)="close(VIDEOS)">Onboarding videos</button>
            <button mat-button (click)="close(WALKTHROUGH)">Learn the basics</button>
            <button mat-button (click)="close(TUTORIAL)">Tutorials</button>
        </mat-dialog-actions>
    `,
    styles: [`
        .content { display: flex; flex-direction: column; gap: 14px; }
        .lead { margin: 0; line-height: 1.55; color: var(--ink, #1e2233); }
        .key { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 8px 20px; margin: 0; padding: 12px 14px; list-style: none; background: var(--bg, #f4f5f9); border-radius: 8px; }
        .key li { display: flex; align-items: center; gap: 10px; font-size: 13px; line-height: 1.4; }
        .key app-node-svg { flex: none; }
        h3 { margin: 4px 0 0; font-size: 15px; font-weight: 600; }
        .starts { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; }
        .start { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; padding: 16px; font: inherit; text-align: left; color: var(--ink, #1e2233);
            background: var(--paper, #fff); border: 1px solid var(--line, #e7e9f0); border-radius: 10px; cursor: pointer; transition: border-color .2s ease, box-shadow .2s ease; }
        .start:hover, .start:focus-visible { border-color: var(--teal, #00a9a5); box-shadow: 0 6px 18px rgba(0, 169, 165, .15); }
        .start:focus-visible { outline: 2px solid var(--teal, #00a9a5); outline-offset: 2px; }
        .start mat-icon { width: 28px; height: 28px; font-size: 28px; color: #00706d; }
        .start-title { font-size: 15px; font-weight: 600; }
        .start-text { font-size: 13px; line-height: 1.5; color: var(--slate, #5b6172); }
        .note { display: flex; align-items: flex-start; gap: 8px; margin: 0; font-size: 13px; line-height: 1.5; color: var(--slate, #5b6172); }
        .note mat-icon { flex: none; width: 18px; height: 18px; font-size: 18px; color: #00706d; }
        .actions { flex-wrap: wrap; gap: 4px; }
        .help-label { margin-right: auto; padding-left: 8px; font-size: 13px; color: var(--slate, #5b6172); }
    `],
})
export class NewModelDialogComponent {
    readonly nodeKey = NODE_KEY
    readonly CREATE = ONBOARDING_CHOICE.CREATE
    readonly TOUR = ONBOARDING_CHOICE.TOUR
    readonly VIDEOS = ONBOARDING_CHOICE.VIDEOS
    readonly WALKTHROUGH = ONBOARDING_CHOICE.WALKTHROUGH
    readonly TUTORIAL = ONBOARDING_CHOICE.TUTORIAL

    constructor(private dialogRef: MatDialogRef<NewModelDialogComponent, ONBOARDING_CHOICE>) {}

    static open(dialog:MatDialog) {
        return dialog.open<NewModelDialogComponent, void, ONBOARDING_CHOICE>(NewModelDialogComponent, {width: '760px', maxWidth: '95vw', maxHeight: '90vh', hasBackdrop: true})
    }

    close(choice?:ONBOARDING_CHOICE) {
        this.dialogRef.close(choice)
    }
}
